import { useEffect, useState } from "react";
import { endpoints, fmtUSD } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Percent, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  product: "",
  tier: "",
  markup_percent: 10,
  min_tcv_usd: 0,
  max_tcv_usd: 1000000,
  notes: "",
};

export default function RateCards() {
  const [cards, setCards] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create; obj = edit

  const load = () => endpoints.rateCards().then(setCards);
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({ ...EMPTY });
    setEditOpen(true);
  };
  const openEdit = (rc) => {
    setEditing({ ...rc });
    setEditOpen(true);
  };

  const save = async () => {
    const payload = {
      ...editing,
      markup_percent: Number(editing.markup_percent),
      min_tcv_usd: Number(editing.min_tcv_usd),
      max_tcv_usd: Number(editing.max_tcv_usd),
    };
    if (editing.id) {
      await endpoints.updateRateCard(editing.id, payload);
      toast.success("Rate card updated");
    } else {
      await endpoints.createRateCard(payload);
      toast.success("Rate card created");
    }
    setEditOpen(false);
    load();
  };

  const remove = async (id) => {
    await endpoints.deleteRateCard(id);
    toast("Rate card deleted");
    load();
  };

  return (
    <div className="max-w-[1200px] px-12 py-14" data-testid="page-rate-cards">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="tx-overline mb-2">Module M4 · markup</div>
          <h1 className="font-heading font-black text-4xl mb-2">Rate Cards</h1>
          <p className="text-zinc-600 text-[14px] max-w-2xl">
            The agent looks up the applicable rate card for each deal via the{" "}
            <code className="text-orange-600 font-mono">get_rate_card</code> tool
            and applies your markup automatically when it drafts a CPPO.
          </p>
        </div>
        <Button
          onClick={openNew}
          data-testid="new-rate-card-btn"
          className="bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm gap-2"
        >
          <Plus className="w-4 h-4" /> New rate card
        </Button>
      </div>

      <div className="border border-zinc-200 bg-zinc-50">
        <table className="w-full text-[13px]">
          <thead className="border-b border-zinc-200">
            <tr>
              {["Product", "Tier", "Markup", "TCV band", "Notes", ""].map((h) => (
                <th key={h} className="tx-overline px-4 py-3 font-normal text-left">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cards.map((c) => (
              <tr
                key={c.id}
                className="border-b border-zinc-200 last:border-b-0 hover:bg-zinc-100 cursor-pointer"
                onClick={() => openEdit(c)}
                data-testid={`rate-card-${c.id}`}
              >
                <td className="px-4 py-3">
                  <div className="font-medium">{c.product}</div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{c.id}</div>
                </td>
                <td className="px-4 py-3">{c.tier}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1 font-heading font-black text-xl text-orange-600">
                    {c.markup_percent}
                    <Percent className="w-4 h-4 mb-0.5" />
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-[12px] text-zinc-600">
                  {fmtUSD(c.min_tcv_usd)} – {fmtUSD(c.max_tcv_usd)}
                </td>
                <td className="px-4 py-3 text-zinc-600 text-[12px] max-w-md truncate">
                  {c.notes}
                </td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`delete-rc-${c.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(c.id);
                    }}
                    className="p-1.5 hover:bg-zinc-200 rounded-sm text-zinc-500 hover:text-rose-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-sm max-w-lg" data-testid="rate-card-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editing?.id ? "Edit rate card" : "New rate card"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="tx-overline">Product (use * for any)</Label>
                <Input
                  data-testid="rc-product"
                  value={editing.product}
                  onChange={(e) => setEditing({ ...editing, product: e.target.value })}
                  className="mt-2 bg-zinc-50 border-zinc-200 font-mono rounded-sm"
                />
              </div>
              <div>
                <Label className="tx-overline">Tier</Label>
                <Input
                  value={editing.tier}
                  onChange={(e) => setEditing({ ...editing, tier: e.target.value })}
                  className="mt-2 bg-zinc-50 border-zinc-200 rounded-sm"
                  placeholder="e.g. Enterprise, Renewal (ABO)"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="tx-overline">Markup %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editing.markup_percent}
                    onChange={(e) => setEditing({ ...editing, markup_percent: e.target.value })}
                    className="mt-2 bg-zinc-50 border-zinc-200 font-mono rounded-sm"
                  />
                </div>
                <div>
                  <Label className="tx-overline">Min TCV</Label>
                  <Input
                    type="number"
                    value={editing.min_tcv_usd}
                    onChange={(e) => setEditing({ ...editing, min_tcv_usd: e.target.value })}
                    className="mt-2 bg-zinc-50 border-zinc-200 font-mono rounded-sm"
                  />
                </div>
                <div>
                  <Label className="tx-overline">Max TCV</Label>
                  <Input
                    type="number"
                    value={editing.max_tcv_usd}
                    onChange={(e) => setEditing({ ...editing, max_tcv_usd: e.target.value })}
                    className="mt-2 bg-zinc-50 border-zinc-200 font-mono rounded-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="tx-overline">Notes</Label>
                <Input
                  value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  className="mt-2 bg-zinc-50 border-zinc-200 rounded-sm"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              className="border-zinc-300 hover:bg-zinc-100 rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              data-testid="save-rate-card-btn"
              className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm gap-2"
            >
              <Save className="w-4 h-4" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
